import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PlatformProvider } from '../../context/PlatformContext';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', roles: ['admin', 'management', 'developer'], icon: 'D' },
  { path: '/compare', label: 'Compare', roles: ['admin', 'management', 'developer'], icon: 'V' },
  { path: '/alerts', label: 'Alerts', roles: ['admin', 'management', 'developer'], icon: 'A' },
  { path: '/admin/users', label: 'Users', roles: ['admin'], icon: 'U' },
];

// Chevron SVG icons
function ChevronLeft() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const visibleItems = NAV_ITEMS.filter(item => item.roles.includes(user?.role));
  const roleBadge = {
    admin: 'badge-blue',
    management: 'badge-green',
    developer: 'badge-yellow',
  };

  return (
    <PlatformProvider>
    <div className="min-h-screen flex bg-surface-900">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-[68px]'} bg-surface-800 border-r border-surface-600 flex flex-col transition-all duration-200 relative`}>
        {/* Logo + Collapse Toggle */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-surface-600">
          {sidebarOpen ? (
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white leading-tight">VZY.ONE</h2>
              <p className="text-[10px] text-gray-500 leading-tight">Stability Dashboard</p>
            </div>
          ) : (
            <span className="text-xl font-bold text-brand-500 mx-auto">V</span>
          )}

          {/* Collapse toggle - prominent, always visible */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`flex items-center justify-center w-7 h-7 rounded-md bg-surface-600 hover:bg-surface-500 text-gray-300 hover:text-white transition-colors ${
              !sidebarOpen ? 'absolute -right-3.5 top-4 z-10 shadow-lg bg-surface-700 hover:bg-brand-600 border border-surface-500' : 'flex-shrink-0 ml-2'
            }`}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/management'}
              title={!sidebarOpen ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${sidebarOpen ? 'px-3' : 'justify-center px-1'} py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-600/20 text-brand-400 border border-brand-500/20'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-surface-700'
                }`
              }
            >
              <span className={`w-8 h-8 rounded-lg bg-surface-600 flex items-center justify-center text-xs font-bold flex-shrink-0 ${sidebarOpen ? 'mr-3' : ''}`}>
                {item.icon}
              </span>
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User info at bottom */}
        {sidebarOpen && (
          <div className="px-3 py-3 border-t border-surface-600 text-xs text-gray-500">
            <div className="truncate">{user?.name || user?.email}</div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 bg-surface-800 border-b border-surface-600 flex items-center justify-between px-6">
          <h1 className="text-lg font-semibold text-gray-200">Stability Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className={roleBadge[user?.role] || 'badge-blue'}>{user?.role}</span>
            <span className="text-sm text-gray-400">{user?.name || user?.email}</span>
            <button onClick={logout} className="text-sm text-gray-500 hover:text-red-400 transition-colors">
              Logout
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
    </PlatformProvider>
  );
}
